// AI Generated Test
import { Component, OnInit } from '@angular/core';
import { StatsRepoService } from '@client/app/services/api/stats-repo/stats-repo.service';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { DateTimeService } from '@client/app/services/date-time/date-time.service';
import { PipeService } from '@client/app/filters/pipe-transformation.service';

@Component({
    selector: 'domain-dashboard-statistics',
    standalone: false,
    template: `
        <div class="card panel-monsido-card max-height d-flex flex-column">
            <a class="p-0" [uiSref]="'base.customer.domain.statistics.summary'" [attr.aria-label]="'Site Analytics' | translate">
                <mon-panel-header [pHeader]="'Site Analytics' | translate">
                    <div class="text-right">
                        <div class="btn btn-link">
                            <span class="pl-1 fas fa-chevron-right" aria-hidden="true"></span>
                        </div>
                    </div>
                </mon-panel-header>
            </a>
            <div class="card-body flex-grow-1 row d-flex align-items-stretch">
                <div class="col-48">
                    <mon-spinner [loading]="loading">
                        <div class="row my-3 mx-0" *ngIf="!loading">
                            <div class="col-48 inner-l inner-r inner-b-small">
                                <p>
                                    <span translate>Total visits in the last 30 days:</span>
                                    <span class="text-grey">
                                        {{ totalVisits | monNumber:'0,0' }}
                                    </span>
                                </p>
                            </div>
                            <div class="col-48 px-4">
                                <mon-chart-line [height]="100"
                                            [data]="lineChartData"
                                            [labels]="lineChartLabels"
                                            [series]="lineChartSeries"
                                            [colors]="lineChartColors"
                                            [override]="lineChartOverride"
                                            [options]="lineChartOptions">
                                </mon-chart-line>
                                <div class="clearfix"></div>
                                <div class="d-flex justify-content-between">
                                    <span class="text-grey font-size-0-9"
                                          *ngIf="firstDate">
                                        {{firstDate.date | date:'MMM dd'}}
                                    </span>
                                    <span class="text-grey font-size-0-9"
                                          *ngIf="lastDate">
                                        {{lastDate.date | date:'MMM dd'}}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </mon-spinner>
                </div>
            </div>
        </div>
    `,
})
export class DomainDashboardStatisticsComponent implements OnInit {
    loading: boolean = true;
    dates: any[] = [];
    lineChartSeries: any[] = [];
    lineChartData: any[] = [];
    lineChartColors: any[] = [];
    lineChartLabels: any[] = [];
    lineChartOverride: any[] = [];
    lineChartOptions: any = {};
    firstDate: any = null;
    lastDate: any = null;
    totalVisits: number = 0;

    constructor (
        private ng2StatsRepoService: StatsRepoService,
        private translateService: TranslateService,
        private ng2DateTimeService: DateTimeService,
        private ng2PipeService: PipeService,
    ) {}

    ngOnInit (): void {
        this.activate();
    }

    activate (): void {
        this.loading = true;
        this.dates = [];

        this.lineChartSeries = [];
        this.lineChartData = [];
        this.lineChartColors = [];
        this.lineChartLabels = [];
        this.lineChartOverride = [];
        this.lineChartOptions = [];
        this.firstDate = null;
        this.lastDate = null;
        this.totalVisits = 0;

        this.getPage();
    }

    getPage (): void {
        const params = {
            to: this.ng2DateTimeService.format(
                this.ng2DateTimeService.endOf(
                    this.ng2DateTimeService.substractDuration(undefined, 1, 'days'),
                    'day',
                ),
                'YYYY-MM-DD',
            ),
            from: this.ng2DateTimeService.format(
                this.ng2DateTimeService.substractDuration(undefined, 30, 'days'),
                'YYYY-MM-DD',
            ),
        };
        this.loading = true;

        this.ng2StatsRepoService.getSummaryPerDate(params)
            .then((data: any) => {
                data.forEach((date: any) => {
                    this.dates.push({
                        date: date.date,
                        visits: date.visits,
                    });
                });
                this.totalVisits = this.dates.reduce((sum, date) => sum + parseInt(date.visits), 0);
                this.setupChart();
            })
            .finally(() => {
                this.loading = false;
            });
    }

    setupChart (): void {
        this.lineChartSeries = [this.translateService.getString('Visits')];
        this.lineChartLabels = [];
        this.lineChartData = [];
        this.lineChartColors = ['rgba(87,188,219,1)'];
        this.lineChartOverride = [
            {
                label: this.translateService.getString('Visits'),
                type: 'line',
                fill: false,
                lineTension: 0,
                borderColor: '#57BCDB',
                xAxisID: 'domain-dashboard-statistics-x',
                yAxisID: 'domain-dashboard-statistics-y',
                pointRadius: 0,
            },
        ];

        this.lineChartOptions = {
            tooltips: {
                mode: 'label',
                intersect: false,
                callbacks: {
                    label: (tooltipItem: any, data: any) => {
                        const set = data.datasets[tooltipItem.datasetIndex];
                        return set.label + ': ' + this.ng2PipeService.get('monNumber')(set.data[tooltipItem.index], '0,0');
                    },
                },
            },
            scales: {
                yAxes: [
                    {
                        id: 'domain-dashboard-statistics-y',
                        type: 'linear',
                        position: 'left',
                        ticks: {
                            beginAtZero: true,
                            suggestedMax: 4,
                            maxTicksLimit: 5,
                            callback: (value: any) => this.ng2PipeService.get('monNumber')(value, '0,0'),
                            min: 0,
                        },
                    },
                ],
                xAxes: [
                    {
                        id: 'domain-dashboard-statistics-x',
                        display: false,
                    },
                ],
            },
            pointRadius: 0,
        };

        this.dates.forEach((date) => {
            this.lineChartLabels.push(this.ng2PipeService.get('userDate')(date.date));
        });

        const chartData = this.dates.map((date) => date.visits);
        this.lineChartData.push(chartData);

        if (this.dates.length > 0) {
            this.firstDate = this.dates[0];
            this.lastDate = this.dates[this.dates.length - 1];
        } else {
            this.firstDate = null;
            this.lastDate = null;
        }

        this.loading = false;
    }
}
